import type { GraphpadWidgetConfig } from '../graphpad.model';

/**
 * Preset demonstrating annotation primitives with a draggable tangent.
 *
 * Features demonstrated:
 * 1. **Free text label**: Slope value displayed at a fixed position
 * 2. **Popup label**: Coordinate label attached to the tangent touch point (moves with drag)
 * 3. **Dynamic arrow**: Arrow pointing to the tangent point with a "Drag me!" label
 */
export const TEST_ANNOTATIONS_PRESET: GraphpadWidgetConfig = {
  id: 'test-annotations',
  type: 'graphpad',
  params: {
    f: {
      type: 'quadratic-vertex',
      params: { a: 1, h: 2, k: -3 },
    },

    'f-tangent': {
      type: 'tangent',
      params: { of: 'f', x: 4 },
    },

    // Free text showing the current slope value
    'label-slope': {
      type: 'label',
      params: {
        text: 'Slope = {f-tangent.slope}',
        precision: 1,
        position: { x: -3.5, y: 7 },
      },
    },

    // Popup label attached to the tangent touch point (follows drag)
    'label-tangent-info': {
      type: 'label',
      params: {
        text: 'x = {f-tangent.x}',
        precision: 2,
        of: 'f-tangent',
      },
    },

    // Arrow pointing to the tangent touch point with "Drag me!" label
    'arrow-to-tangent': {
      type: 'line',
      params: {
        from: { x: 7, y: 7 },
        to: { x: 'f-tangent.x', y: 'f-tangent.y' },
      },
    },
  },
  display: {
    canvas: { color: 'neutral-0' },
    math: { defaultPrecision: 2 },
    coordinateSystem: {
      boundingbox: { xStart: -6, xEnd: 10, yStart: -8, yEnd: 8 },
      grid: { type: 'lines', visible: false },
    },
    elements: {
      f: {
        curve: { color: 'sky', strokeWidth: 3 },
      },

      'f-tangent': {
        subparts: {
          touchPoint: {
            point: { color: 'amber' },
          },
          tangentLine: {
            line: { color: 'amber', strokeWidth: 2 },
          },
        },
      },

      'label-slope': {
        label: {
          variant: 'primary',
          color: 'pink',
          fontSize: 15,
          textAlign: 'left',
        },
      },

      'label-tangent-info': {
        label: {
          variant: 'secondary',
          color: 'amber',
          offset: [0, -20],
          popup: true,
        },
      },

      'arrow-to-tangent': {
        line: {
          color: 'neutral',
          strokeWidth: 2,
          dash: 2,
          arrows: { last: true },
          label: { text: 'Drag me!', position: 'top' },
        },
      },
    },
  },
};
